import { Link } from "react-router-dom";
import {
  ArrowUpRight, Building2, Snowflake, Leaf, Droplets, Layers, Sparkles,
  ShieldCheck, Clock, MapPin, Award, Wrench, Lightbulb
} from "lucide-react";
import { FadeUp, Counter } from "../components/Motion";
import PageMeta from "../components/PageMeta";

const HERO_IMG = "https://images.unsplash.com/photo-1776359913316-659ad961e7c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200";
const MAINT_IMG = "https://images.unsplash.com/photo-1761637823293-f96835ec5038?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const SNOW_IMG  = "https://images.unsplash.com/photo-1648003945425-c01118b74a78?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const HARDS_IMG = "https://images.unsplash.com/photo-1761637823407-ef47925c2714?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const CLIENT_TYPES = [
  "Developers", "Retail Properties", "Office Buildings", "Industrial Sites",
  "Multi-Family / Condos", "Hotels & Hospitality", "Restaurants", "Property Managers",
];

const SERVICE_GROUPS = [
  {
    title: "Design & Construction",
    items: [
      { icon: Building2,  name: "Landscape Construction" },
      { icon: Layers,     name: "Commercial Hardscaping" },
      { icon: Layers,     name: "Retaining Walls" },
      { icon: Wrench,     name: "Grading & Site Prep" },
    ],
  },
  {
    title: "Grounds Maintenance",
    items: [
      { icon: Leaf,       name: "Lawn & Bed Maintenance" },
      { icon: Sparkles,   name: "Pest, Weed & Fertilization" },
      { icon: Leaf,       name: "Seasonal Clean-ups" },
      { icon: Leaf,       name: "Pruning & Plant Care" },
    ],
  },
  {
    title: "Snow & Ice",
    link: { to: "/commercial/snow-removal", label: "Snow Removal Details →" },
    items: [
      { icon: Snowflake,  name: "Commercial Snow Removal" },
      { icon: Snowflake,  name: "Ice Management" },
      { icon: Snowflake,  name: "Parking Lot & Walkway Service" },
      { icon: Clock,      name: "24/7 Storm Response" },
    ],
  },
  {
    title: "Systems & Features",
    items: [
      { icon: Droplets,   name: "Smart Irrigation" },
      { icon: Lightbulb,  name: "Outdoor Lighting" },
      { icon: Sparkles,   name: "Flower Beds & Seasonal Color" },
      { icon: Leaf,       name: "Trees & Shrubs" },
    ],
  },
];

const PrimaryCTA = ({ children = "Request Commercial Proposal", to = "/estimate" }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-2 px-7 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-widest uppercase transition-colors duration-300"
  >
    {children} <ArrowUpRight className="w-4 h-4" />
  </Link>
);

const GhostCTA = ({ children, to }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-2 px-7 py-4 border border-[#F5F3EF]/40 hover:bg-[#F5F3EF] hover:text-[#151515] text-[#F5F3EF] text-sm tracking-widest uppercase transition-all duration-300"
  >
    {children} <ArrowUpRight className="w-4 h-4" />
  </Link>
);

function Section({ id, eyebrow, title, body, image, reverse = false, cta }) {
  return (
    <section id={id} className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-[#D8D6D0]/10">
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
        <FadeUp className="lg:col-span-7">
          <div className="aspect-[4/3] overflow-hidden">
            <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" />
          </div>
        </FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-5">
          <div className="overline text-[#8C1D30] mb-4">{eyebrow}</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-none mb-6">{title}</h2>
          <div className="text-[#D8D6D0]/75 leading-relaxed space-y-4">{body}</div>
          {cta && <div className="mt-8">{cta}</div>}
        </FadeUp>
      </div>
    </section>
  );
}

export default function Commercial() {
  return (
    <div data-testid="commercial-page">
      <PageMeta
        title="Commercial Landscaping Edmonton — Snow, Maintenance & Construction"
        description="Commercial landscape construction, year-round grounds maintenance, and 24/7 snow & ice management for developers, retail, office, and multi-family properties across Edmonton and Alberta."
        path="/commercial"
        image="https://images.unsplash.com/photo-1776359913316-659ad961e7c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
      />
      {/* HERO */}
      <section className="relative h-[85vh] min-h-[600px] w-full overflow-hidden">
        <img src={HERO_IMG} alt="Commercial landscaping" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative h-full flex flex-col justify-end pb-20 px-6 md:px-12 lg:px-16">
          <FadeUp>
            <div className="overline text-[#D8D6D0]/80 mb-6">Commercial Landscaping</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] max-w-5xl">
              Get back to business — <span className="italic text-[#D8D6D0]">we'll handle the rest.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#D8D6D0]/80 max-w-2xl leading-relaxed">
              Full-service commercial landscape construction, year-round maintenance, and snow & ice management for developers, retail, office, and multi-family properties across Alberta.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <PrimaryCTA />
              <GhostCTA to="/projects">View Commercial Projects</GhostCTA>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* INTRO */}
      <section className="px-6 md:px-12 lg:px-16 py-24 border-y border-[#D8D6D0]/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <FadeUp className="lg:col-span-7">
            <div className="overline text-[#8C1D30] mb-4">First Impressions</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-none mb-8">
              A well-designed exterior is <span className="italic text-[#D8D6D0]">leasing velocity, brand, and retention.</span>
            </h2>
            <p className="text-[#D8D6D0]/75 leading-relaxed text-lg">
              Premium commercial landscaping increases tenant retention, leasing velocity and asset value. We design and execute outdoor environments that elevate how your property is perceived — and back it with year-round maintenance and snow service that lets you focus on running your business.
            </p>
            <p className="text-[#D8D6D0]/65 leading-relaxed mt-4">
              Whether you're planning a new development or upgrading an existing portfolio, every project is tailored around your property, brand, and unique site conditions.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <PrimaryCTA />
              <Link to="/projects" className="link-underline text-sm uppercase tracking-widest text-[#D8D6D0]/80 self-center">Commercial Portfolio →</Link>
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5 space-y-8">
            <div>
              <Award className="w-6 h-6 text-[#8C1D30] mb-3" strokeWidth={1.25} />
              <div className="font-serif-display text-4xl"><Counter value={50} suffix="+" /></div>
              <div className="overline text-[#D8D6D0]/50 mt-2">Commercial Properties Maintained</div>
            </div>
            <div>
              <Clock className="w-6 h-6 text-[#8C1D30] mb-3" strokeWidth={1.25} />
              <div className="font-serif-display text-4xl">24/7</div>
              <div className="overline text-[#D8D6D0]/50 mt-2">Snow & Ice Storm Response</div>
            </div>
            <div>
              <ShieldCheck className="w-6 h-6 text-[#8C1D30] mb-3" strokeWidth={1.25} />
              <div className="font-serif-display text-4xl">$5M</div>
              <div className="overline text-[#D8D6D0]/50 mt-2">Commercial Liability Coverage</div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <FadeUp>
          <div className="max-w-3xl mb-16">
            <div className="overline text-[#8C1D30] mb-4">Services</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none">
              One contractor. <span className="italic text-[#D8D6D0]">Every season.</span>
            </h2>
          </div>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D8D6D0]/10">
          {SERVICE_GROUPS.map((group, gi) => (
            <FadeUp key={group.title} delay={gi * 0.05}>
              <div className="bg-[#151515] p-8 lg:p-10 h-full">
                <div className="overline text-[#D8D6D0]/40 mb-6">0{gi + 1}</div>
                <div className="font-serif-display text-2xl md:text-3xl mb-8">{group.title}</div>
                <ul className="space-y-4">
                  {group.items.map((it, i) => {
                    const Ico = it.icon;
                    return (
                      <li key={it.name} className="flex items-center gap-3 text-sm text-[#D8D6D0]/80 hover:text-[#F5F3EF] transition-colors">
                        <Ico className="w-4 h-4 text-[#8C1D30]" strokeWidth={1.25} />
                        {it.name}
                      </li>
                    );
                  })}
                </ul>
                {group.link && (
                  <Link
                    to={group.link.to}
                    data-testid="commercial-snow-removal-link"
                    className="link-underline inline-block mt-8 text-xs uppercase tracking-widest text-[#E2B85A] hover:text-[#F5F3EF] transition-colors"
                  >
                    {group.link.label}
                  </Link>
                )}
              </div>
            </FadeUp>
          ))}
          <FadeUp delay={0.25} className="md:col-span-2 lg:col-span-2">
            <Link to="/estimate" className="group bg-[#0B2B1F] p-8 lg:p-10 h-full flex flex-col justify-between hover:bg-[#8C1D30] transition-colors duration-500">
              <div>
                <div className="overline text-[#F5F3EF]/60 mb-6">05</div>
                <div className="font-serif-display text-2xl md:text-3xl">Request a Proposal.</div>
                <p className="text-sm text-[#D8D6D0]/70 mt-4">Single visit, single line-item proposal, single contractor.</p>
              </div>
              <div className="mt-8 flex items-center gap-2 text-xs uppercase tracking-widest text-[#F5F3EF]">
                Commercial Proposal <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* CLIENT TYPES */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-[#0B2B1F]">
        <FadeUp>
          <div className="overline text-[#D8D6D0]/70 mb-4">Who We Serve</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-none max-w-4xl mb-12">
            From a single building to <span className="italic">a multi-property portfolio.</span>
          </h2>
        </FadeUp>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#F5F3EF]/10 max-w-5xl">
          {CLIENT_TYPES.map((c, i) => (
            <FadeUp key={c} delay={i * 0.03}>
              <div className="bg-[#0B2B1F] p-6 md:p-8 text-[#F5F3EF] text-sm uppercase tracking-widest h-full flex items-center hover:bg-[#151515] transition-colors duration-500">
                {c}
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <p className="text-[#D8D6D0]/80 mt-10 max-w-3xl leading-relaxed">
            Every property is different. We adapt around layout, usage, foot-traffic, drainage and lot grading — and execute with crews dedicated to commercial standards of timing, safety, and reporting.
          </p>
        </FadeUp>
      </section>

      {/* DEEP-DIVE: CONSTRUCTION */}
      <Section
        id="construction"
        eyebrow="Landscape Construction"
        title="Turnkey commercial builds."
        image={HARDS_IMG}
        body={
          <>
            <p>
              From site prep and grading to hardscape, planting, irrigation and lighting — we deliver completed commercial landscapes on schedule and within budget. Engineered systems built for high traffic, harsh winters, and decades of service.
            </p>
            <p>
              We work directly with developers, GCs, architects and property managers — coordinating with civil and structural where required, executing to spec.
            </p>
          </>
        }
        cta={<PrimaryCTA />}
      />

      {/* DEEP-DIVE: MAINTENANCE */}
      <Section
        id="maintenance"
        eyebrow="Property Maintenance"
        title="Year-round grounds management."
        image={MAINT_IMG}
        reverse
        body={
          <>
            <p>
              Comprehensive maintenance contracts: lawn care, pest and weed management, fertilization, pruning, seasonal flower rotations, irrigation start-up and winterization.
            </p>
            <p>
              One dedicated account manager. Monthly reporting. Photographic site checks. We make your grounds invisible to complaint and visible to compliment.
            </p>
          </>
        }
        cta={<PrimaryCTA>Get Maintenance Quote</PrimaryCTA>}
      />

      {/* DEEP-DIVE: SNOW */}
      <Section
        id="snow"
        eyebrow="Snow & Ice Management"
        title="Open by sunrise. Every storm."
        image={SNOW_IMG}
        body={
          <>
            <p>
              Edmonton winters are unforgiving. Our commercial snow removal service keeps parking lots, walkways, entrances, and high-traffic areas safe and accessible — no matter the snowfall.
            </p>
            <p>
              Weather-monitored response. Pre-treatment with brine. Dedicated routes per property. Service logs with photos delivered after every event for your liability records.
            </p>
          </>
        }
        cta={<PrimaryCTA>Snow Contract Inquiry</PrimaryCTA>}
      />

      {/* TRUST BAR */}
      <section className="px-6 md:px-12 lg:px-16 py-24 border-t border-[#D8D6D0]/10">
        <FadeUp>
          <div className="text-center mb-12">
            <div className="overline text-[#8C1D30] mb-3">Compliance</div>
            <h2 className="font-serif-display text-3xl md:text-4xl">Fully licensed, insured & WCB-covered.</h2>
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="nc-card p-8 text-center">
              <ShieldCheck className="w-7 h-7 text-[#8C1D30] mx-auto mb-3" strokeWidth={1.25} />
              <div className="font-serif-display text-xl">$5M Liability</div>
              <p className="overline text-[#D8D6D0]/50 mt-2">Commercial coverage</p>
            </div>
            <div className="nc-card p-8 text-center">
              <Award className="w-7 h-7 text-[#8C1D30] mx-auto mb-3" strokeWidth={1.25} />
              <div className="font-serif-display text-xl">Licensed</div>
              <p className="overline text-[#D8D6D0]/50 mt-2">City of Edmonton</p>
            </div>
            <div className="nc-card p-8 text-center">
              <MapPin className="w-7 h-7 text-[#8C1D30] mx-auto mb-3" strokeWidth={1.25} />
              <div className="font-serif-display text-xl">WCB Alberta</div>
              <p className="overline text-[#D8D6D0]/50 mt-2">Active account</p>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <FadeUp>
          <div className="max-w-4xl">
            <div className="overline text-[#8C1D30] mb-4">Begin</div>
            <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95]">
              One contractor. <span className="italic text-[#D8D6D0]">All four seasons.</span>
            </h2>
            <p className="text-[#D8D6D0]/65 mt-8 max-w-2xl text-lg">
              Request a proposal — we'll respond within one business day with scope, schedule, and itemized pricing.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <PrimaryCTA />
              <a href="tel:+17806557604" className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-[#F5F3EF]/40 hover:bg-[#F5F3EF] hover:text-[#151515] text-[#F5F3EF] text-sm tracking-widest uppercase transition-all duration-300">
                (780) 655-7604
              </a>
              <a href="mailto:northcrestedmonton@gmail.com?subject=Commercial Proposal Request" className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-[#F5F3EF]/40 hover:bg-[#F5F3EF] hover:text-[#151515] text-[#F5F3EF] text-sm tracking-widest uppercase transition-all duration-300">
                Email Proposal
              </a>
            </div>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
